// What the browser sent for the whole call, and how Twilio said it arrived.
//
// The SDK's 1 Hz sample only describes the inbound leg. `media-source` is the energy
// captured before encoding, and `remote-inbound-rtp` is Twilio's receiver report on OUR
// stream — together they separate "the mic delivered nothing" from "it was lost upstream".

import { peerConnectionOf, type StatLike, type StatsReportLike } from './call-setup-timing';

export const MEDIA_POLL_MS = 2_000;
// A digitally silent source adds no energy at all; a live quiet mic after noise
// suppression still adds far more than this per poll.
const ZERO_ENERGY_EPS = 1e-9;

export interface MediaStatsReport {
  upstream_pc_missing: boolean;
  upstream_polls: number;
  upstream_poll_errors: number;
  // How much of the call the stats cover, in ms since accept.
  upstream_last_poll_ms: number | null;
  upstream_packets_sent: number | null;
  // Post-processing energy at the encoder input, summed across replaced sources.
  upstream_source_energy: number | null;
  upstream_source_resets: number;
  // Time during which captured energy did not advance. Null with one poll.
  upstream_zero_energy_ms: number | null;
  upstream_remote_packets_lost: number | null;
  upstream_remote_loss_ratio: number | null;
  upstream_remote_jitter_max_ms: number | null;
  upstream_remote_rtt_avg_ms: number | null;
}

interface MediaStats {
  report: MediaStatsReport;
  lastPollAt: number | null;
  lastEnergy: number | null;
  lastSourceId: string | null;
  energyBase: number;
  zeroEnergyMs: number;
}

export function newMediaStats(): MediaStats {
  return {
    report: {
      upstream_pc_missing: false,
      upstream_polls: 0,
      upstream_poll_errors: 0,
      upstream_last_poll_ms: null,
      upstream_packets_sent: null,
      upstream_source_energy: null,
      upstream_source_resets: 0,
      upstream_zero_energy_ms: null,
      upstream_remote_packets_lost: null,
      upstream_remote_loss_ratio: null,
      upstream_remote_jitter_max_ms: null,
      upstream_remote_rtt_avg_ms: null,
    },
    lastPollAt: null,
    lastEnergy: null,
    lastSourceId: null,
    energyBase: 0,
    zeroEnergyMs: 0,
  };
}

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const round = (v: number, dp: number): number => Math.round(v * 10 ** dp) / 10 ** dp;

export function foldMediaStats(
  s: MediaStats,
  stats: StatsReportLike,
  msSinceAccept: number | null,
  now: number,
): void {
  const r = s.report;
  const first: Record<string, StatLike> = {};
  const byId = new Map<string, StatLike>();
  stats.forEach((stat) => {
    if (stat.id) byId.set(stat.id, stat);
    // Chromium reports `kind`; older engines only `mediaType`.
    if (!stat.type || first[stat.type] || (stat['kind'] ?? stat['mediaType']) !== 'audio') return;
    first[stat.type] = stat;
  });

  r.upstream_packets_sent = num(first['outbound-rtp']?.['packetsSent']) ?? r.upstream_packets_sent;

  // Just after a track swap the old source can still be listed, so follow the sender to
  // the one it is encoding.
  const sourceRef = first['outbound-rtp']?.['mediaSourceId'];
  const source =
    (typeof sourceRef === 'string' ? byId.get(sourceRef) : undefined) ?? first['media-source'];
  const energy = num(source?.['totalAudioEnergy']);
  if (energy != null) {
    const prev = s.lastEnergy;
    const id = source?.id ?? null;
    // A replaced capture track is a new source with its own counter. Its id says so; the
    // counter going backwards is the fallback where no id is reported.
    const replaced =
      prev != null &&
      (id != null && s.lastSourceId != null ? id !== s.lastSourceId : energy < prev);
    s.lastSourceId = id;
    if (replaced) {
      s.energyBase += prev;
      r.upstream_source_resets += 1;
    } else if (prev != null && energy - prev <= ZERO_ENERGY_EPS) {
      s.zeroEnergyMs += Math.max(0, now - (s.lastPollAt ?? now));
    }
    s.lastEnergy = energy;
    r.upstream_source_energy = round(s.energyBase + energy, 6);
  }

  const remote = first['remote-inbound-rtp'];
  if (remote) {
    r.upstream_remote_packets_lost = num(remote['packetsLost']) ?? r.upstream_remote_packets_lost;
    const jitter = num(remote['jitter']);
    if (jitter != null) {
      const ms = Math.round(jitter * 1000);
      r.upstream_remote_jitter_max_ms = Math.max(r.upstream_remote_jitter_max_ms ?? 0, ms);
    }
    // Cumulative, so polling the same receiver report twice does not bias the mean.
    const total = num(remote['totalRoundTripTime']);
    const count = num(remote['roundTripTimeMeasurements']);
    if (total != null && count) r.upstream_remote_rtt_avg_ms = Math.round((total / count) * 1000);
  }
  const sent = r.upstream_packets_sent;
  const lost = r.upstream_remote_packets_lost;
  r.upstream_remote_loss_ratio = lost == null || !sent ? null : round(lost / sent, 4);

  r.upstream_polls += 1;
  r.upstream_last_poll_ms = msSinceAccept;
  r.upstream_zero_energy_ms = r.upstream_polls < 2 ? null : Math.round(s.zeroEnergyMs);
  s.lastPollAt = now;
}

export interface MediaPollerOptions {
  call: unknown;
  stats: MediaStats;
  sinceAccept: () => number | null;
  onPoll?: () => void;
  setIntervalFn?: (fn: () => void, ms: number) => unknown;
  clearIntervalFn?: (id: unknown) => void;
}

// Polls immediately, then every MEDIA_POLL_MS. The returned stop makes no further reads,
// and a report resolving after it is dropped: its row has already been emitted.
export function attachMediaStatsPoller(o: MediaPollerOptions): () => void {
  const pc = peerConnectionOf(o.call);
  if (!pc) {
    o.stats.report.upstream_pc_missing = true;
    return () => {};
  }
  const r = o.stats.report;
  let stopped = false;
  const poll = (): void => {
    if (stopped) return;
    try {
      o.onPoll?.();
    } catch {
      r.upstream_poll_errors += 1;
    }
    pc.getStats().then(
      (stats) => {
        if (stopped) return;
        try {
          foldMediaStats(o.stats, stats, o.sinceAccept(), performance.now());
        } catch {
          r.upstream_poll_errors += 1;
        }
      },
      () => {
        if (!stopped) r.upstream_poll_errors += 1;
      },
    );
  };
  poll();
  const id = (o.setIntervalFn ?? setInterval)(poll, MEDIA_POLL_MS);
  return () => {
    stopped = true;
    (o.clearIntervalFn ?? ((i: unknown) => clearInterval(i as ReturnType<typeof setInterval>)))(id);
  };
}
